import { useEffect, useState, useMemo, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CreditCard, Banknote, Receipt, Plus, Trash2, Wallet, Search, Save, CheckCircle2, Download } from "lucide-react";
import { toast } from "sonner";

/**
 * Onglet "Gestion des frais d'inscription"
 * - Tarification (1 enfant, 2 enfants, 3+ enfants)
 * - Liste des familles avec montant dû / payé / restant
 * - Dialogue d'édition par famille (méthode : comptant / chèques / espèces)
 */
export default function FeesTab({ selectedYearId }) {
  const [tiers, setTiers] = useState(null);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedYearId ? { year_id: selectedYearId } : {};
      const [tRes, fRes] = await Promise.all([
        api.get("/settings/fees"),
        api.get("/admin/family-payments", { params }),
      ]);
      setTiers(tRes.data);
      setFamilies(fRes.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [selectedYearId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let arr = families;
    if (statusFilter !== "all") arr = arr.filter((f) => f.status === statusFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((f) =>
        (f.parent_name || "").toLowerCase().includes(q) ||
        (f.parent_email || "").toLowerCase().includes(q) ||
        (f.children || []).some((c) => `${c.prenom} ${c.nom}`.toLowerCase().includes(q))
      );
    }
    return arr;
  }, [families, statusFilter, search]);

  const totals = useMemo(() => {
    const due = families.reduce((acc, f) => acc + (f.amount_due || 0), 0);
    const paid = families.reduce((acc, f) => acc + (f.amount_paid || 0), 0);
    const nPaid = families.filter((f) => f.status === "paid").length;
    const nPartial = families.filter((f) => f.status === "partial").length;
    const nUnpaid = families.filter((f) => f.status === "unpaid").length;
    const nExempt = families.filter((f) => f.status === "exempted").length;
    return { due, paid, remaining: Math.max(0, due - paid), nPaid, nPartial, nUnpaid, nExempt, total: families.length };
  }, [families]);

  if (loading || !tiers) {
    return <div className="bg-white border border-[#EAE6DF] rounded-2xl p-10 text-center text-[#5c6374]">Chargement…</div>;
  }

  return (
    <div className="space-y-6" data-testid="fees-tab">
      <FeeTiersCard tiers={tiers} onSaved={load} selectedYearId={selectedYearId} />

      {/* Résumé */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard icon={Wallet} label="Total dû" value={`${totals.due.toFixed(0)} €`} />
        <SummaryCard icon={CheckCircle2} label="Total perçu" value={`${totals.paid.toFixed(0)} €`} highlight={totals.paid > 0} />
        <SummaryCard icon={CreditCard} label="Restant à percevoir" value={`${totals.remaining.toFixed(0)} €`} accent={totals.remaining > 0} />
        <SummaryCard icon={Receipt} label="Familles" value={`${totals.nPaid}/${totals.total} payées`} />
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-[#EAE6DF] rounded-2xl p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8a92a3]" />
          <Input
            placeholder="Rechercher (parent, email, enfant)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-full pl-9"
            data-testid="fees-search"
          />
        </div>
        <div className="flex gap-1 bg-[#F4F1EB] rounded-full p-1 flex-wrap">
          {[
            { v: "all", label: `Toutes (${totals.total})` },
            { v: "unpaid", label: `Non payées (${totals.nUnpaid})` },
            { v: "partial", label: `Partielles (${totals.nPartial})` },
            { v: "paid", label: `Payées (${totals.nPaid})` },
            { v: "exempted", label: `Exemptées (${totals.nExempt})` },
          ].map((o) => (
            <button
              key={o.v}
              onClick={() => setStatusFilter(o.v)}
              className={`px-3 py-1.5 text-xs rounded-full transition-colors ${statusFilter === o.v ? "bg-white text-[#1A2547] font-medium shadow-sm" : "text-[#5c6374] hover:text-[#1A2547]"}`}
              data-testid={`fees-filter-${o.v}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* Liste familles */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-[#EAE6DF] rounded-2xl p-10 text-center text-[#5c6374]">
          Aucune famille à afficher pour ce filtre.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((fam) => (
            <FamilyRow key={fam.parent_email} family={fam} onSaved={load} yearId={selectedYearId} />
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, highlight, accent }) {
  return (
    <div className={`bg-white border rounded-xl p-4 ${accent ? "border-[#C86444]" : highlight ? "border-[#1E7A4D]" : "border-[#EAE6DF]"}`}>
      <Icon className={`w-4 h-4 ${accent ? "text-[#C86444]" : highlight ? "text-[#1E7A4D]" : "text-[#1E3A8A]"}`} strokeWidth={1.5} />
      <div className="mt-2 text-xl font-[Outfit] font-semibold text-[#1A2547]">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a968f] mt-0.5">{label}</div>
    </div>
  );
}

function FeeTiersCard({ tiers, onSaved, selectedYearId }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(tiers);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { if (open) setForm(tiers); }, [open, tiers]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = selectedYearId ? { year_id: selectedYearId } : {};
      const r = await api.get("/admin/family-payments/export.csv", { params, responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv; charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      a.download = m ? m[1] : `paiements_familles_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Export CSV téléchargé");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setExporting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings/fees", {
        one_child: parseFloat(form.one_child),
        two_children: parseFloat(form.two_children),
        three_plus_children: parseFloat(form.three_plus_children),
      });
      toast.success("Tarification mise à jour");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#EAE6DF] rounded-2xl p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-[Outfit] font-semibold text-base text-[#1A2547]">Tarification annuelle</h3>
          <p className="text-xs text-[#5c6374] mt-1">Tarifs par famille selon le nombre d'enfants inscrits.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            onClick={handleExport}
            disabled={exporting}
            data-testid="export-payments-csv"
            title="Télécharger un fichier CSV à ouvrir avec Excel/LibreOffice pour la comptabilité"
          >
            <Download className="w-4 h-4 mr-1.5" /> {exporting ? "Export…" : "Export CSV"}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="rounded-full" data-testid="edit-tiers-btn">Modifier</Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Modifier la tarification</DialogTitle></DialogHeader>
            <DialogDescription className="text-xs">Définissez le tarif total que paye une famille selon le nombre d'enfants validés cette année.</DialogDescription>
            <form onSubmit={submit} className="space-y-3 mt-2">
              <div>
                <Label className="text-sm">1 enfant (€)</Label>
                <Input type="number" min="0" step="0.5" required value={form.one_child} onChange={(e) => setForm({ ...form, one_child: e.target.value })} className="rounded-xl mt-1.5" data-testid="tier-one" />
              </div>
              <div>
                <Label className="text-sm">2 enfants (€)</Label>
                <Input type="number" min="0" step="0.5" required value={form.two_children} onChange={(e) => setForm({ ...form, two_children: e.target.value })} className="rounded-xl mt-1.5" data-testid="tier-two" />
              </div>
              <div>
                <Label className="text-sm">3 enfants et plus (€)</Label>
                <Input type="number" min="0" step="0.5" required value={form.three_plus_children} onChange={(e) => setForm({ ...form, three_plus_children: e.target.value })} className="rounded-xl mt-1.5" data-testid="tier-three" />
              </div>
              <Button type="submit" disabled={saving} className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="tier-submit">
                <Save className="w-4 h-4 mr-1.5" /> {saving ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-4">
        <TierTile n={1} amount={tiers.one_child} />
        <TierTile n={2} amount={tiers.two_children} />
        <TierTile n="3+" amount={tiers.three_plus_children} />
      </div>
      </div>
    </div>
  );
}

function TierTile({ n, amount }) {
  return (
    <div className="bg-[#FCFBF8] border border-[#EAE6DF] rounded-xl px-3 py-2.5">
      <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-[#8a968f]">{n} enf.</div>
      <div className="mt-1 text-lg sm:text-2xl font-[Outfit] font-semibold text-[#1A2547]">{Number(amount).toFixed(0)} €</div>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "paid") return <Badge className="bg-[#E0F2E9] text-[#1E7A4D] hover:bg-[#E0F2E9] border-0 rounded-full">Payée</Badge>;
  if (status === "partial") return <Badge className="bg-[#FFF5E5] text-[#C86444] hover:bg-[#FFF5E5] border-0 rounded-full">Partielle</Badge>;
  if (status === "exempted") return <Badge className="bg-[#E0E7FF] text-[#1E3A8A] hover:bg-[#E0E7FF] border-0 rounded-full">Exemptée</Badge>;
  return <Badge className="bg-[#FBE5E4] text-[#B0413E] hover:bg-[#FBE5E4] border-0 rounded-full">Non payée</Badge>;
}

const EXEMPTION_LABELS = {
  teacher_child: "Enfant de professeur",
  volunteer_child: "Enfant de bénévole",
};

function ExemptionPill({ kind }) {
  if (!kind) return null;
  return (
    <Badge className="bg-[#E0E7FF] text-[#1E3A8A] hover:bg-[#E0E7FF] border-0 rounded-full inline-flex items-center text-[10px]">
      Exemptée — {EXEMPTION_LABELS[kind] || kind}
    </Badge>
  );
}

function MethodPill({ method }) {
  if (!method) return null;
  const label = method === "comptant" ? "Un seul chèque" : method === "cheques" ? "Plusieurs chèques" : "Espèces";
  const icon = method === "cheques" || method === "comptant" ? <Receipt className="w-3 h-3 mr-1" /> : <Banknote className="w-3 h-3 mr-1" />;
  return <Badge className="bg-[#E0E7FF] text-[#1E3A8A] hover:bg-[#E0E7FF] border-0 rounded-full inline-flex items-center">{icon}{label}</Badge>;
}

function FamilyRow({ family, onSaved, yearId }) {
  return (
    <div className="bg-white border border-[#EAE6DF] rounded-2xl p-4" data-testid={`family-row-${family.parent_email}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-[Outfit] font-semibold text-base text-[#1A2547]">{family.parent_name || family.parent_email}</h4>
            <StatusBadge status={family.status} />
            <ExemptionPill kind={family.exemption_kind} />
            <MethodPill method={family.method} />
            <Badge className="bg-[#F4F1EB] text-[#5c6374] hover:bg-[#F4F1EB] border-0 rounded-full text-[10px]">{family.n_children} enfant{family.n_children > 1 ? "s" : ""}</Badge>
          </div>
          <div className="text-xs text-[#5c6374] mt-1 truncate">{family.parent_email} · {family.parent_phone || "—"}</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {family.children.map((c) => (
              <span key={c.request_id} className="inline-flex items-center gap-1.5 bg-[#FCFBF8] border border-[#EAE6DF] rounded-full px-2.5 py-1 text-xs text-[#1A2547]">
                {c.prenom} {c.nom}
              </span>
            ))}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs uppercase tracking-[0.18em] text-[#8a968f]">Dû / Payé</div>
          <div className="mt-1 font-[Outfit] font-semibold text-lg text-[#1A2547]">
            {family.amount_paid.toFixed(0)} <span className="text-[#5c6374]">/ {family.amount_due.toFixed(0)} €</span>
          </div>
          {family.amount_remaining > 0 && (
            <div className="text-xs text-[#C86444] mt-0.5">Reste {family.amount_remaining.toFixed(0)} €</div>
          )}
          <PaymentDialog family={family} onSaved={onSaved} yearId={yearId} />
        </div>
      </div>
      {family.notes && (
        <div className="mt-3 text-xs text-[#5c6374] bg-[#FCFBF8] border border-[#EAE6DF] rounded-xl px-3 py-2 italic">
          « {family.notes} »
        </div>
      )}
    </div>
  );
}

function PaymentDialog({ family, onSaved, yearId }) {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState(family.method || "comptant");
  const [amountPaid, setAmountPaid] = useState(family.amount_paid || 0);
  // Génère un UID stable pour la clé React (les chèques n'ont pas d'id côté backend)
  const ensureUids = (arr) => (arr || []).map((c) => c._uid ? c : { ...c, _uid: (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)) });
  const [checks, setChecks] = useState(() => ensureUids(family.checks?.length ? family.checks : [{ amount: 0, label: "" }]));
  const [notes, setNotes] = useState(family.notes || "");
  const [exemption, setExemption] = useState(family.exemption_kind || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setMethod(family.method || "comptant");
      setAmountPaid(family.amount_paid || 0);
      setChecks(ensureUids(family.checks?.length ? family.checks : [{ amount: 0, label: "" }]));
      setNotes(family.notes || "");
      setExemption(family.exemption_kind || "");
    }
  }, [open, family]);

  const checksTotal = useMemo(
    () => (checks || []).reduce((acc, c) => acc + (parseFloat(c.amount) || 0), 0),
    [checks]
  );

  // Si méthode = chèques, on déduit amount_paid de la somme des chèques renseignés
  useEffect(() => {
    if (method === "cheques") setAmountPaid(checksTotal);
  }, [method, checksTotal]);

  const addCheck = () => setChecks((cur) => [...cur, { amount: 0, label: `Chèque ${cur.length + 1}`, _uid: (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)) }]);
  const removeCheck = (i) => setChecks((cur) => cur.filter((_, idx) => idx !== i));
  const updateCheck = (i, field, value) => setChecks((cur) => cur.map((c, idx) => idx === i ? { ...c, [field]: value } : c));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = exemption
        ? { method: null, amount_paid: 0, checks: [], notes: notes || null, exemption_kind: exemption }
        : {
            method,
            amount_paid: parseFloat(amountPaid) || 0,
            checks: method === "cheques" ? checks.filter((c) => parseFloat(c.amount) > 0).map((c) => ({ amount: parseFloat(c.amount), label: c.label || null })) : [],
            notes: notes || null,
            exemption_kind: null,
          };
      const params = yearId ? { year_id: yearId } : {};
      await api.put(`/admin/family-payments/${encodeURIComponent(family.parent_email)}`, payload, { params });
      toast.success(exemption ? "Exemption enregistrée" : "Paiement enregistré");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const cancelPayment = async () => {
    if (!window.confirm("Annuler le paiement enregistré ?\n\nLe montant perçu repassera à 0 € et la famille sera marquée « Non payée ».")) return;
    setSaving(true);
    try {
      const params = yearId ? { year_id: yearId } : {};
      await api.put(`/admin/family-payments/${encodeURIComponent(family.parent_email)}`, {
        method: null,
        amount_paid: 0,
        checks: [],
        notes: notes || null,
        exemption_kind: null,
        mark_children_paid: false,
      }, { params });
      toast.success("Paiement annulé");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const markFullyPaid = async () => {
    setSaving(true);
    try {
      const params = yearId ? { year_id: yearId } : {};
      await api.put(`/admin/family-payments/${encodeURIComponent(family.parent_email)}`, {
        method: method || "comptant",
        amount_paid: family.amount_due,
        checks: [],
        notes,
        mark_children_paid: true,
      }, { params });
      toast.success("Famille marquée payée");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-full mt-2 bg-[#1E3A8A] hover:bg-[#14265E] text-white" data-testid={`open-payment-${family.parent_email}`}>
          <CreditCard className="w-4 h-4 mr-1.5" /> {family.amount_paid > 0 ? "Modifier" : "Enregistrer paiement"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{family.parent_name || family.parent_email}</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-xs">
          Montant dû : <strong className="text-[#1A2547]">{family.amount_due.toFixed(0)} €</strong> ({family.n_children} enfant{family.n_children > 1 ? "s" : ""})
        </DialogDescription>
        <form onSubmit={submit} className="space-y-4">
          {/* Exemption de cotisation */}
          <div className="bg-[#FCFBF8] border border-[#EAE6DF] rounded-xl p-3 space-y-2">
            <Label className="text-sm font-medium text-[#1A2547]">Exemption de cotisation</Label>
            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`exempt-${family.parent_email}`}
                  checked={exemption === ""}
                  onChange={() => setExemption("")}
                  className="accent-[#1E3A8A]"
                  data-testid="exempt-none"
                />
                <span className="text-xs text-[#1A2547]">Aucune exemption (cotisation due)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`exempt-${family.parent_email}`}
                  checked={exemption === "teacher_child"}
                  onChange={() => setExemption("teacher_child")}
                  className="accent-[#1E3A8A]"
                  data-testid="exempt-teacher"
                />
                <span className="text-xs text-[#1A2547]">Enfant d'un professeur — <em>cotisation = 0 €</em></span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`exempt-${family.parent_email}`}
                  checked={exemption === "volunteer_child"}
                  onChange={() => setExemption("volunteer_child")}
                  className="accent-[#1E3A8A]"
                  data-testid="exempt-volunteer"
                />
                <span className="text-xs text-[#1A2547]">Enfant d'un bénévole actif — <em>cotisation = 0 €</em></span>
              </label>
            </div>
            {exemption && (
              <p className="text-[10px] text-[#1E7A4D] mt-1">Famille exemptée : aucun paiement n'est requis. Le statut sera marqué « Exemptée ».</p>
            )}
          </div>

          {!exemption && (
            <>
              {/* Méthode */}
              <div>
                <Label className="text-sm">Mode de paiement</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="comptant">Un seul chèque</SelectItem>
                <SelectItem value="cheques">Plusieurs chèques</SelectItem>
                <SelectItem value="especes">Espèces</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Chèques détaillés */}
          {method === "cheques" && (
            <div className="bg-[#FCFBF8] border border-[#EAE6DF] rounded-xl p-3 space-y-2" data-testid="checks-list">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Chèques ({checks.length})</Label>
                <Button type="button" size="sm" variant="ghost" className="rounded-full text-xs" onClick={addCheck} data-testid="add-check">
                  <Plus className="w-3 h-3 mr-1" /> Ajouter un chèque
                </Button>
              </div>
              {checks.map((c, i) => (
                <div key={c._uid || i} className="flex items-center gap-2" data-testid={`check-${i}`}>
                  <Input
                    type="text"
                    placeholder={`Libellé (ex: Chèque ${i + 1})`}
                    value={c.label || ""}
                    onChange={(e) => updateCheck(i, "label", e.target.value)}
                    className="rounded-xl flex-1 text-sm"
                  />
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    placeholder="Montant"
                    value={c.amount}
                    onChange={(e) => updateCheck(i, "amount", e.target.value)}
                    className="rounded-xl w-28 text-sm"
                    data-testid={`check-amount-${i}`}
                  />
                  <span className="text-xs text-[#5c6374]">€</span>
                  {checks.length > 1 && (
                    <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => removeCheck(i)}>
                      <Trash2 className="w-4 h-4 text-[#B0413E]" />
                    </Button>
                  )}
                </div>
              ))}
              <div className="flex justify-between items-center pt-2 border-t border-[#EAE6DF] text-sm">
                <span className="text-[#5c6374]">Total chèques</span>
                <strong className="text-[#1A2547]">{checksTotal.toFixed(2)} €</strong>
              </div>
              {Math.abs(checksTotal - family.amount_due) > 0.01 && (
                <p className="text-xs text-[#C86444]">
                  {checksTotal < family.amount_due
                    ? `Reste ${(family.amount_due - checksTotal).toFixed(2)} € à couvrir`
                    : `Surplus de ${(checksTotal - family.amount_due).toFixed(2)} €`}
                </p>
              )}
            </div>
          )}

          {/* Montant payé (manuel si pas chèques) */}
          {method !== "cheques" && (
            <div>
              <Label className="text-sm">Montant perçu (€)</Label>
              <Input
                type="number"
                min="0"
                step="0.5"
                value={amountPaid}
                onChange={(e) => setAmountPaid(e.target.value)}
                className="rounded-xl mt-1.5"
                data-testid="amount-paid"
              />
              <p className="text-xs text-[#5c6374] mt-1">Indiquez 0 pour non payé, ou le montant total dû pour marquer payée.</p>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label className="text-sm">Notes (optionnel)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="rounded-xl mt-1.5"
              placeholder="Ex: chèque encaissé en janvier, paiement reporté…"
            />
          </div>
            </>
          )}

          <div className="flex flex-col gap-2 pt-2">
            <div className="flex gap-2">
              {!exemption && (
                <Button type="button" variant="outline" className="rounded-full flex-1" onClick={markFullyPaid} disabled={saving} data-testid="mark-fully-paid">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Marquer payée
                </Button>
              )}
              <Button type="submit" className="rounded-full flex-1 bg-[#1E3A8A] hover:bg-[#14265E]" disabled={saving} data-testid="save-payment">
                <Save className="w-4 h-4 mr-1.5" /> {saving ? "Enregistrement…" : (exemption ? "Enregistrer l'exemption" : "Enregistrer")}
              </Button>
            </div>
            {(family.amount_paid > 0 || family.status === "paid" || family.status === "partial") && (
              <Button type="button" variant="ghost" className="rounded-full w-full text-[#B0413E] hover:text-[#B0413E] hover:bg-[#FBE5E4]" onClick={cancelPayment} disabled={saving} data-testid="cancel-payment">
                <Trash2 className="w-4 h-4 mr-1.5" /> Annuler le paiement enregistré
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
